import { useState, useCallback, useEffect } from 'react';
import { AccountService, AccountDeletionResult } from '../services/AccountService';
import { useAuth } from './useAuth';
import { isNativeApp } from '../utils/platform';

export interface UseAccountDeletionReturn {
  deleteAccount: (password: string, captchaToken?: string) => Promise<void>;
  isSubmitting: boolean;
  error: string | null;
  success: string | null;
  confirmationRequired: boolean;
  showConfirmation: () => void;
  hideConfirmation: () => void;
  clearMessages: () => void;
  validationError: string | null;
}

/**
 * Custom hook for handling account deletion with confirmation flow
 * Requirements: 4.1 - Account deletion with confirmation flow
 */
export const useAccountDeletion = (): UseAccountDeletionReturn => {
  const { signOut } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirmationRequired, setConfirmationRequired] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Auto-clear success message after 3 seconds (shorter for deletion)
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const showConfirmation = useCallback(() => {
    setConfirmationRequired(true);
    setError(null);
    setValidationError(null);
  }, []);

  const hideConfirmation = useCallback(() => {
    setConfirmationRequired(false);
    setError(null);
    setValidationError(null);
  }, []);

  const deleteAccount = useCallback(async (password: string, captchaToken?: string): Promise<void> => {
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);
    setValidationError(null);

    let result: AccountDeletionResult | null = null;

    try {
      // Validate password input
      if (!password || password.trim().length === 0) {
        setValidationError('Password is required to confirm account deletion');
        throw new Error('Password is required');
      }

      if (!isNativeApp() && !captchaToken) {
        setValidationError('Please complete the CAPTCHA challenge');
        throw new Error('Captcha is required');
      }

      result = await AccountService.deleteAccount(password, captchaToken);
      
      if (result.success) {
        setSuccess(result.message || 'Account deletion initiated successfully');
        setConfirmationRequired(false);
        
        // Sign out user after successful deletion
        try {
          await signOut();
        } catch (signOutError) {
          console.warn('Failed to sign out after account deletion:', signOutError);
          // Don't throw here as the deletion was successful
        }
      } else {
        // Handle specific error cases
        if (result.message.toLowerCase().includes('password')) {
          setValidationError(result.message);
        } else if (result.message.toLowerCase().includes('captcha')) {
          setValidationError(result.message);
        } else {
          setError(result.message);
        }
        throw new Error(result.message);
      }
    } catch (err) {
      if (err instanceof Error && err.message !== 'Password is required') {
        const errorMessage = err.message || 'Failed to delete account';
        // Only set general error if we haven't already set a validation error
        if (!result || !result.message.toLowerCase().includes('password')) {
          setError(errorMessage);
        }
      }
      throw err; // Re-throw to allow component to handle it
    } finally {
      setIsSubmitting(false);
    }
  }, [signOut]);

  const clearMessages = useCallback(() => {
    setError(null);
    setSuccess(null);
    setValidationError(null);
  }, []);

  return {
    deleteAccount,
    isSubmitting,
    error,
    success,
    confirmationRequired,
    showConfirmation,
    hideConfirmation,
    clearMessages,
    validationError
  };
};
